import { RequestSessionStatus, SessionStatus } from '@sentry/types';
import { dropUndefinedKeys, logger, uuid4 } from '@sentry/utils';
import { getCurrentHub } from './hub';
/**
 * @inheritdoc
 */

var Session =
/** @class */
function () {
  function Session(context) {
    this.errors = 0;
    this.sid = uuid4();
    this.timestamp = Date.now();
    this.started = Date.now();
    this.duration = 0;
    this.status = SessionStatus.Ok;
    this.init = true;

    if (context) {
      this.update(context);
    }
  }
  /** JSDoc */
  // eslint-disable-next-line complexity


  Session.prototype.update = function (context) {
    if (context === void 0) {
      context = {};
    }

    if (context.user) {
      if (context.user.ip_address) {
        this.ipAddress = context.user.ip_address;
      }

      if (!context.did) {
        this.did = context.user.id || context.user.email || context.user.username;
      }
    }

    this.timestamp = context.timestamp || Date.now();

    if (context.sid) {
      // Good enough uuid validation. — Kamil
      this.sid = context.sid.length === 32 ? context.sid : uuid4();
    }

    if (context.init !== undefined) {
      this.init = context.init;
    }

    if (context.did) {
      this.did = "" + context.did;
    }

    if (typeof context.started === 'number') {
      this.started = context.started;
    }

    if (typeof context.duration === 'number') {
      this.duration = context.duration;
    } else {
      this.duration = this.timestamp - this.started;
    }

    if (context.release) {
      this.release = context.release;
    }

    if (context.environment) {
      this.environment = context.environment;
    }

    if (context.ipAddress) {
      this.ipAddress = context.ipAddress;
    }

    if (context.userAgent) {
      this.userAgent = context.userAgent;
    }

    if (typeof context.errors === 'number') {
      this.errors = context.errors;
    }

    if (context.status) {
      this.status = context.status;
    }
  };
  /** JSDoc */


  Session.prototype.close = function (status) {
    if (status) {
      this.update({
        status: status
      });
    } else if (this.status === SessionStatus.Ok) {
      this.update({
        status: SessionStatus.Exited
      });
    } else {
      this.update();
    }
  };
  /** JSDoc */


  Session.prototype.toJSON = function () {
    return dropUndefinedKeys({
      sid: "" + this.sid,
      init: this.init,
      started: new Date(this.started).toISOString(),
      timestamp: new Date(this.timestamp).toISOString(),
      status: this.status,
      errors: this.errors,
      did: typeof this.did === 'number' || typeof this.did === 'string' ? "" + this.did : undefined,
      duration: this.duration,
      attrs: dropUndefinedKeys({
        release: this.release,
        environment: this.environment,
        ip_address: this.ipAddress,
        user_agent: this.userAgent
      })
    });
  };

  return Session;
}();

export { Session };
/**
 * @inheritdoc
 */

var SessionFlusher =
/** @class */
function () {
  function SessionFlusher(transport, attrs) {
    var _this = this;

    this.flushTimeout = 60;
    this._pendingAggregates = {};
    this._isEnabled = true;
    this._transport = transport; // Call to setInterval, so that flush is called every 60 seconds

    this._intervalId = setInterval(function () {
      return _this.flush();
    }, this.flushTimeout * 1000);
    this._sessionAttrs = attrs;
  }
  /** Sends session aggregates to Transport */


  SessionFlusher.prototype.sendSessionAggregates = function (sessionAggregates) {
    if (!this._transport.sendSession) {
      logger.warn("Dropping session because custom transport doesn't implement sendSession");
      return;
    }

    this._transport.sendSession(sessionAggregates).then(null, function (reason) {
      logger.error("Error while sending session: " + reason);
    });
  };
  /** Checks if `pendingAggregates` has entries, and if it does flushes them by calling `sendSessions` */


  SessionFlusher.prototype.flush = function () {
    var sessionAggregates = this.getSessionAggregates();

    if (sessionAggregates.aggregates.length === 0) {
      return;
    }

    this._pendingAggregates = {};
    this.sendSessionAggregates(sessionAggregates);
  };
  /** Massages the entries in `pendingAggregates` and returns aggregated sessions */


  SessionFlusher.prototype.getSessionAggregates = function () {
    var _this = this;

    var aggregates = Object.keys(this._pendingAggregates).map(function (key) {
      return _this._pendingAggregates[parseInt(key)];
    });
    var sessionAggregates = {
      attrs: this._sessionAttrs,
      aggregates: aggregates
    };
    return dropUndefinedKeys(sessionAggregates);
  };
  /** JSDoc */


  SessionFlusher.prototype.close = function () {
    clearInterval(this._intervalId);
    this._isEnabled = false;
    this.flush();
  };
  /**
   * Wrapper function for _incrementSessionStatusCount that checks if the instance of SessionFlusher is enabled then
   * fetches the session status of the request from `Scope.getRequestSession().status` on the scope and passes them to
   * `_incrementSessionStatusCount` along with the start date
   */


  SessionFlusher.prototype.incrementSessionStatusCount = function () {
    var _a, _b;

    if (!this._isEnabled) {
      return;
    }

    var scope = getCurrentHub().getScope();
    var requestSession = (_a = scope) === null || _a === void 0 ? void 0 : _a.getRequestSession();

    if (requestSession && requestSession.status) {
      this._incrementSessionStatusCount(requestSession.status, new Date()); // This is not entirely necessarily but is added as a safe guard to indicate the bounds of a request and so in
      // case captureRequestSession is called more than once to prevent double count


      (_b = scope) === null || _b === void 0 ? void 0 : _b.setRequestSession(undefined);
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    }
  };
  /**
   * Increments status bucket in pendingAggregates buffer (internal state) corresponding to status of
   * the session received
   */


  SessionFlusher.prototype._incrementSessionStatusCount = function (status, date) {
    // Truncate minutes and seconds on Session Started attribute to have one minute bucket keys
    var sessionStartedTrunc = new Date(date).setSeconds(0, 0);
    this._pendingAggregates[sessionStartedTrunc] = this._pendingAggregates[sessionStartedTrunc] || {}; // corresponds to aggregated sessions in one specific minute bucket
    // for example, {"started":"2021-03-16T08:00:00.000Z","exited":4, "errored": 1}

    var aggregationCounts = this._pendingAggregates[sessionStartedTrunc];

    if (!aggregationCounts.started) {
      aggregationCounts.started = new Date(sessionStartedTrunc).toISOString();
    }

    switch (status) {
      case RequestSessionStatus.Errored:
        aggregationCounts.errored = (aggregationCounts.errored || 0) + 1;
        return aggregationCounts.errored;

      case RequestSessionStatus.Ok:
        aggregationCounts.exited = (aggregationCounts.exited || 0) + 1;
        return aggregationCounts.exited;

      case RequestSessionStatus.Crashed:
        aggregationCounts.crashed = (aggregationCounts.crashed || 0) + 1;
        return aggregationCounts.crashed;
    }
  };

  return SessionFlusher;
}();

export { SessionFlusher };